import { HorizontalScrollView } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import { convertToGeoLocation, type GeoLocation } from "@src/appV2/Location";
import {
  type ExtraWorkedTimeRequestEntity,
  type ExtraWorkedTimeRequestShift,
  type ExtraWorkedTimeRequestStatus,
  ExtraWorkedTimeRequestStatus as ExtraWorkedTimeRequestStatusEnum,
  type ExtraWorkedTimeRequestStatusUpdater,
} from "@src/appV2/Shifts/MandatoryBreakPolicy/api/useExtraWorkedTimeRequests";
import { compareDesc, differenceInDays, parseISO } from "date-fns";
import { useMemo } from "react";

import { usePatchExtraWorkedTimeRequestMetadata } from "../../api/usePatchExtraWorkedTimeRequestMetadata";
import { useWorkerUnverifiedShifts } from "../../UnverifiedShifts/useWorkerUnverifiedShifts";
import { useGetExtraWorkedTimeRequests } from "../api/useGetExtraWorkedTimeRequests";
import { type ShiftActionParams } from "../Page";
import { ExtraWorkedTimeRequestBanner } from "./ExtraWorkedTimeRequestBanner";
import { UnverifiedShiftBanner } from "./UnverifiedShiftBanner";

const TIMESHEET_SUBMISSION_WINDOW_IN_DAYS = 30;

interface TransformedRequest {
  id: string;
  status: ExtraWorkedTimeRequestStatus.APPROVED | ExtraWorkedTimeRequestStatus.DECLINED;
  workplaceName: string;
  workplaceTimezone: string;
  shiftDate: Date;
  shiftId: string;
  updatedAt: string;
  isVerified: boolean;
  isFullShiftPayment?: boolean;
  statusUpdatedBy?: ExtraWorkedTimeRequestStatusUpdater;
  statusUpdateReason?: string | null;
}

interface TransformedUnverifiedShift {
  shiftId: string;
  workplaceId: string | undefined;
  workplaceName: string;
  workplaceTimezone: string | undefined;
  shiftDate: Date;
  shiftEndAt: string;
  isMissedPunchRequestEnabled: boolean;
  workplaceLocation: GeoLocation | undefined;
}

interface ShiftHistoryBannerListProps {
  onUploadTimesheetClick: (params: ShiftActionParams) => void;
  onRequestShiftAdjustmentClick: (params: ShiftActionParams) => Promise<void>;
  isCheckingGeofence: boolean;
  selectedShift: ShiftActionParams | undefined;
}

export function ShiftHistoryBannerList(props: ShiftHistoryBannerListProps) {
  const {
    onUploadTimesheetClick,
    onRequestShiftAdjustmentClick,
    isCheckingGeofence,
    selectedShift,
  } = props;

  const { data: extraWorkedTimeRequestsData } = useGetExtraWorkedTimeRequests();

  const { mutate: patchExtraWorkedTimeRequestMetadata } = usePatchExtraWorkedTimeRequestMetadata();

  const extraWorkedTimeRequests = useMemo((): TransformedRequest[] => {
    if (!extraWorkedTimeRequestsData?.data) {
      return [];
    }

    const shiftMap = new Map<string, ExtraWorkedTimeRequestShift>(
      extraWorkedTimeRequestsData.included
        .filter((item): item is ExtraWorkedTimeRequestShift => item.type === "shift")
        .map((shift) => [shift.id, shift])
    );

    const deduplicatedRequests = [
      ...extraWorkedTimeRequestsData.data
        .toSorted((a, b) =>
          compareDesc(parseISO(a.attributes.updatedAt), parseISO(b.attributes.updatedAt))
        )
        .reduce((map, request) => {
          if (!map.has(request.attributes.shiftId)) {
            map.set(request.attributes.shiftId, request);
          }
          return map;
        }, new Map<string, ExtraWorkedTimeRequestEntity>())
        .values(),
    ];

    return deduplicatedRequests
      .filter(
        ({ attributes: { status }, meta }) =>
          status !== ExtraWorkedTimeRequestStatusEnum.PENDING && !meta?.isNotificationDismissed
      )
      .map(
        ({
          attributes: {
            shiftId,
            status,
            isFullShiftPayment,
            updatedAt,
            statusUpdatedBy,
            statusUpdateReason,
          },
          id,
        }) => {
          const shift = shiftMap.get(shiftId);
          if (!shift) {
            return undefined;
          }

          const { facility, start, verified } = shift.attributes;

          return {
            id,
            status: status as TransformedRequest["status"],
            workplaceName: facility.name,
            workplaceTimezone: facility.tmz,
            shiftDate: parseISO(start),
            shiftId,
            updatedAt,
            isVerified: verified,
            isFullShiftPayment,
            statusUpdatedBy,
            statusUpdateReason,
          };
        }
      )
      .filter(isDefined);
  }, [extraWorkedTimeRequestsData]);

  const { data: unverifiedShiftsData } = useWorkerUnverifiedShifts();

  const unverifiedShifts = useMemo((): TransformedUnverifiedShift[] => {
    if (!unverifiedShiftsData?.response) {
      return [];
    }

    return unverifiedShiftsData.response
      .filter(
        (shift) =>
          (shift.timecard?.files?.length ?? 0) === 0 &&
          !shift.hasPendingFullShiftExtraWorkedTimeRequest &&
          differenceInDays(new Date(), parseISO(shift.start)) <= TIMESHEET_SUBMISSION_WINDOW_IN_DAYS
      )
      .map((shift) => ({
        shiftId: shift._id,
        workplaceId: shift.facility.userId ?? shift.facilityId,
        workplaceName: shift.facility.name,
        workplaceTimezone: shift.facility.tmz,
        shiftDate: parseISO(shift.start),
        shiftEndAt: shift.end,
        isMissedPunchRequestEnabled:
          shift.facility.featureSettings?.isMissedPunchRequestEnabled ?? false,
        workplaceLocation: isDefined(shift.facility.geoLocation?.coordinates)
          ? convertToGeoLocation(shift.facility.geoLocation.coordinates)
          : undefined,
      }));
  }, [unverifiedShiftsData]);

  if (extraWorkedTimeRequests.length === 0 && unverifiedShifts.length === 0) {
    return null;
  }

  return (
    <Box sx={{ marginBottom: 6, marginX: -5 }}>
      <HorizontalScrollView stretch sx={{ px: 5 }}>
        {unverifiedShifts.map((shift) => {
          const actionParams: ShiftActionParams = {
            shiftId: shift.shiftId,
            workplaceId: shift.workplaceId,
            workplaceLocation: shift.workplaceLocation,
            shiftEndAt: shift.shiftEndAt,
            isVerified: false,
            isTimesheetUploaded: false,
          };

          return (
            <UnverifiedShiftBanner
              key={shift.shiftId}
              workplaceName={shift.workplaceName}
              workplaceTimezone={shift.workplaceTimezone}
              shiftDate={shift.shiftDate}
              ctaButtonText={
                shift.isMissedPunchRequestEnabled ? "Submit timesheet" : "Upload timesheet"
              }
              isLoading={selectedShift?.shiftId === shift.shiftId && isCheckingGeofence}
              onCtaClick={() => {
                if (shift.isMissedPunchRequestEnabled) {
                  void onRequestShiftAdjustmentClick(actionParams);
                } else {
                  onUploadTimesheetClick(actionParams);
                }
              }}
            />
          );
        })}
        {extraWorkedTimeRequests.map((request) => (
          <ExtraWorkedTimeRequestBanner
            key={request.id}
            status={request.status}
            workplaceName={request.workplaceName}
            workplaceTimezone={request.workplaceTimezone}
            shiftDate={request.shiftDate}
            shiftId={request.shiftId}
            updatedAt={request.updatedAt}
            isVerified={request.isVerified}
            isFullShiftPayment={request.isFullShiftPayment}
            statusUpdatedBy={request.statusUpdatedBy}
            statusUpdateReason={request.statusUpdateReason}
            onDismiss={() => {
              patchExtraWorkedTimeRequestMetadata({
                extraWorkedTimeRequestId: request.id,
                metadata: { isNotificationDismissed: true },
              });
            }}
          />
        ))}
      </HorizontalScrollView>
    </Box>
  );
}
